/**
 * KK-528 · İmha tutanağı.
 *
 * NE YAPAR: imha bekleyen malın, tutanağa bağlanarak stoktan düşürüldüğü tek
 * ekran. Tutanak (nokta, ürün, miktar, tutar, onaylayan, gerekçe) sistemde
 * kalır; "kayboldu" ile "imha edildi" birbirinden ancak böyle ayrılır.
 *
 * NEDEN KAYNAK LİSTESİ KISITLI (serbest ürün seçimi YOK):
 *   Sunucudaki imhaKaydet(), her satır için
 *   stokYeterliligiDogrula(..., StokDurumu.IMHA_BEKLIYOR, m) çağırır (AK-22) —
 *   yani YALNIZCA "imha bekliyor" kondisyonundaki stok düşürülebilir. Sağlam
 *   stoktan doğrudan imha yapılamaz; yoksa fire, imha kılığına sokulurdu.
 *   Bu yüzden ekran ürün kataloğunu değil, `GET /stok?durum=3` yanıtını
 *   listeler ve kullanıcı yalnızca oradan seçer. Serbest ürün seçtirilseydi
 *   her deneme "stok yetersiz" ile dönerdi.
 *
 * NEDEN NOKTA ÖNCE SEÇİLİR:
 *   İmha nokta bazlıdır; `POST /stok/imha` tek istekte TEK nokta kabul eder ve
 *   KK-611 gereği gider, imhanın gerçekleştiği noktaya yazılır. Farklı
 *   noktaların kalemleri aynı tutanakta karışırsa gider yanlış noktanın
 *   kâr-zararına düşer. Bu yüzden önce nokta, sonra o noktanın kalemleri.
 *
 * NEDEN TehlikeliOnay:
 *   İşlem geri alınamaz ve PARA YAZAR — KK-611 uyarınca gider.kategori = 5
 *   (fire/imha) kaydı oluşur, tutar ortalama maliyetten hesaplanır. Tek
 *   dokunuşla tetiklenmesi, kâr-zarar raporunu sessizce bozan bir hata olurdu.
 *
 * GEREKÇE ALANI: sunucuda `aciklama` opsiyoneldir, bu ekranda ZORUNLUDUR.
 * Tutanağın var olma sebebi "kim, neyi, neden imha etti" sorusuna cevap
 * vermektir; gerekçesiz tutanak o soruyu cevaplamaz.
 *
 * PARA/MİKTAR (ADR-03): Number()/parseFloat() yoktur. Miktarlar string'dir,
 * karşılaştırma @kantin/shared'daki miktar() Decimal'i ile yapılır. Toplam
 * tutar İSTEMCİDE HESAPLANMAZ — sunucunun döndüğü `toplam_tutar` gösterilir.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Buton,
  Kart,
  Rozet,
  Secim,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Tutar,
  Uyari,
} from "@kantin/ui";
import {
  IMHA_YETKILI_ROLLER,
  STOK_DURUM_ADI,
  StokDurumu,
  miktar,
} from "@kantin/shared";
import { apiOku, apiYaz, kapsamDisiMi, yetkisizMi } from "./api.js";
import { useYetkili } from "../../veri/oturum.js";
import {
  AramaKutusu,
  IslemDurumu,
  MetinAlani,
  Sayfa,
  SayfaBasligi,
  SorguDurumu,
  TehlikeliOnay,
  miktarGecerliMi,
  miktarGoster,
  miktarNormalize,
} from "./ortak.js";

// ---------------------------------------------------------------------------

interface StokDagilimi {
  nokta_id: string;
  nokta_ad: string;
  nokta_tur: number;
  depo_mu: boolean;
  durum: number;
  durum_adi: string;
  miktar: string;
}

interface StokUrunu {
  urun_id: string;
  urun_ad: string;
  toplam: string;
  kondisyon: Record<string, string>;
  dagilim: StokDagilimi[];
}

interface ImhaYaniti {
  imha_id: string;
  nokta_id: string;
  toplam_tutar: string;
}

interface Kalem {
  urunId: string;
  urunAd: string;
  noktaId: string;
  noktaAd: string;
  depoMu: boolean;
  mevcut: string;
}

const YETKI_MESAJI =
  "İmha tutanağı yalnızca kantin müdürü ve okul yönetimi tarafından açılabilir.";

const ZINCIR_ANLATIMI =
  "Buraya mal, Karantina ekranındaki “İmhaya ayır” kararıyla veya tarihi geçmiş mal iadesi transferiyle düşer; doğrudan buraya kalem eklenmez.";

/** Satır miktarının hem biçimini hem de tavanını denetler. */
function satirHatasi(deger: string, mevcut: string): string | undefined {
  if (!miktarGecerliMi(deger)) return "Miktarı 5 veya 5,500 biçiminde girin.";
  const istenen = miktar(miktarNormalize(deger));
  if (istenen.lte(0)) return "İmha miktarı sıfırdan büyük olmalıdır.";
  if (istenen.gt(miktar(mevcut))) {
    return `En fazla ${miktarGoster(mevcut)} imha edilebilir.`;
  }
  return undefined;
}

// ---------------------------------------------------------------------------

export function Imha() {
  const istemci = useQueryClient();
  const [noktaId, setNoktaId] = useState("");
  const [arama, setArama] = useState("");
  /** Anahtar = urun_id; anahtarın VARLIĞI seçili olmak demektir. */
  const [secimler, setSecimler] = useState<Record<string, string>>({});
  const [aciklama, setAciklama] = useState("");
  const [gonderildi, setGonderildi] = useState(false);
  const [onayBekliyor, setOnayBekliyor] = useState(false);
  const [sonuc, setSonuc] = useState<ImhaYaniti | null>(null);

  const sorgu = useQuery({
    queryKey: ["stok", "imha-bekliyor"],
    queryFn: () =>
      apiOku<{ urunler: StokUrunu[] }>("/stok", {
        durum: StokDurumu.IMHA_BEKLIYOR,
      }),
  });

  // Sunucu ürün bazında gruplar; tutanak NOKTA bazında yazılır, bu yüzden
  // dağılım satırlarını düzleştiriyoruz (Karantina ekranıyla aynı kalıp).
  const kalemler: Kalem[] = (sorgu.data?.urunler ?? []).flatMap((u) =>
    u.dagilim
      .filter(
        (d) => d.durum === StokDurumu.IMHA_BEKLIYOR && d.miktar !== "0.000",
      )
      .map((d) => ({
        urunId: u.urun_id,
        urunAd: u.urun_ad,
        noktaId: d.nokta_id,
        noktaAd: d.nokta_ad,
        depoMu: d.depo_mu,
        mevcut: d.miktar,
      })),
  );

  // Nokta listesi kalemlerden türetilir: imha bekleyen malı OLMAYAN bir nokta
  // seçilebilir olsaydı, seçim her zaman boş liste gösterirdi.
  const noktalar = [...new Map(kalemler.map((k) => [k.noktaId, k.noktaAd]))]
    .map(([deger, metin]) => ({ deger, metin }))
    .sort((a, b) => a.metin.localeCompare(b.metin, "tr"));

  const noktaKalemleri = kalemler.filter((k) => k.noktaId === noktaId);
  // Arama yalnızca görünümü süzer; tutanağın seçili satırları tam listeden gelir.
  const aramaSorgu = useQuery({
    queryKey: ["stok", "imha-arama", noktaId, arama],
    enabled: Boolean(noktaId && arama.trim()),
    queryFn: () => apiOku<{ urunler: StokUrunu[] }>("/stok", {
      durum: StokDurumu.IMHA_BEKLIYOR, nokta_id: noktaId, arama: arama.trim(),
    }),
  });
  const bulunanIdler = new Set(aramaSorgu.data?.urunler.map((u) => u.urun_id));
  const gorunenKalemler = arama.trim() ? noktaKalemleri.filter((k) => bulunanIdler.has(k.urunId)) : noktaKalemleri;
  const seciliKalemler = noktaKalemleri.filter((k) => k.urunId in secimler);
  const aciklamaHatasi =
    gonderildi && !aciklama.trim()
      ? "Tutanak gerekçesiz olamaz; sebebi yazın."
      : undefined;
  const gecersizSatirVar = seciliKalemler.some(
    (k) => satirHatasi(secimler[k.urunId] ?? "", k.mevcut) !== undefined,
  );

  const gonder = useMutation({
    mutationFn: () =>
      apiYaz<ImhaYaniti>("POST", "/stok/imha", {
        nokta_id: noktaId,
        aciklama: aciklama.trim(),
        satirlar: seciliKalemler.map((k) => ({
          urun_id: k.urunId,
          miktar: miktarNormalize(secimler[k.urunId] ?? k.mevcut),
        })),
      }),
    onSuccess: (yanit) => {
      setSonuc(yanit);
      setSecimler({});
      setAciklama("");
      setGonderildi(false);
      setOnayBekliyor(false);
      void istemci.invalidateQueries({ queryKey: ["stok"] });
    },
  });

  function noktaDegistir(yeni: string) {
    // Nokta değişince eski seçimler taşınamaz: farklı noktaların kalemleri
    // aynı tutanakta birleşemez (KK-611 — gider yanlış noktaya yazılırdı).
    setNoktaId(yeni);
    setSecimler({});
    setGonderildi(false);
    setOnayBekliyor(false);
    // Önceki tutanağın numarası yeni tutanak hazırlanırken ekranda kalmamalı.
    setSonuc(null);
  }

  function secimDegistir(kalem: Kalem, isaretli: boolean) {
    setOnayBekliyor(false);
    setSecimler((onceki) => {
      const yeni = { ...onceki };
      if (isaretli) yeni[kalem.urunId] = kalem.mevcut;
      else delete yeni[kalem.urunId];
      return yeni;
    });
  }

  // GET /stok rol kısıtsızdır (stok.rotalari.ts · personelGerekli()), yani bu
  // listeyi her personel okuyabilir; ekranı yalnızca sunucu hatasına bakarak
  // kapatmak YANLIŞ olurdu. Kapıyı YAZMA yüzeyine koyuyoruz: imhaya ayıran
  // nokta sorumlusu "ayırdığım mal hâlâ bekliyor mu" sorusunu buradan
  // yanıtlayabilmeli, ama KK-528 gereği tutanağı açamamalı.
  const yazabilir = useYetkili(...IMHA_YETKILI_ROLLER);

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="İmha tutanağı"
        aciklama="İmha bekleyen malın tutanağa bağlanarak stoktan düşürülmesi. İşlem geri alınamaz."
      />

      {!yazabilir && (
        <Uyari tur="bilgi" baslik="İmha tutanağı açma yetkiniz yok">
          <p className="m-0">{YETKI_MESAJI}</p>
          <p className="m-0 mt-2">
            İmha bekleyen malı <strong>görebilirsiniz</strong> — ayırdığınız
            malın tutanağa bağlanıp bağlanmadığını buradan izleyin. Tutanağı
            kantin müdürü veya okul yönetimi açar.
          </p>
        </Uyari>
      )}

      <Uyari tur="bilgi" baslik="Mal buraya kendiliğinden düşmez">
        <p className="m-0">{ZINCIR_ANLATIMI}</p>
        <p className="m-0 mt-2">
          İmha edilen malın maliyeti <strong>fire/imha gideri</strong> olarak,
          imhanın yapıldığı noktanın kâr-zararına yazılır. Tutar ortalama
          maliyetten hesaplanır ve sunucu tarafından belirlenir.
        </p>
      </Uyari>

      {sonuc && (
        <Uyari tur="onay" baslik="İmha tutanağı kaydedildi">
          <p className="m-0">
            Tutanak no <span className="rakam font-mono">{sonuc.imha_id}</span>{" "}
            · gider olarak yazılan tutar:{" "}
            <Tutar deger={sonuc.toplam_tutar} yonsuz boy="sm" />
          </p>
        </Uyari>
      )}

      <Kart
        baslik="İmha bekleyen mallar"
        ustBilgi={
          <span className="text-sm text-murekkep-3">
            <span className="rakam font-mono">{kalemler.length}</span> kalem
          </span>
        }
      >
        <SorguDurumu
          yukleniyor={sorgu.isPending}
          hata={sorgu.error}
          bos={kalemler.length === 0}
          bosBaslik="İmha bekleyen mal yok"
          bosAciklama={ZINCIR_ANLATIMI}
          yenile={() => void sorgu.refetch()}
        >
          <div className="flex flex-col gap-4">
            <div className="max-w-sm">
              <Secim
                etiket="Nokta"
                value={noktaId}
                onChange={(e) => noktaDegistir(e.target.value)}
                secenekler={[{ deger: "", metin: "Nokta seçin" }, ...noktalar]}
              />
              <p className="m-0 mt-1 text-xs text-murekkep-3">
                Bir tutanakta tek nokta olur; gider bu noktaya yazılır.
              </p>
            </div>

            {noktaId === "" ? (
              <p className="m-0 text-sm text-murekkep-2">
                Kalemleri listelemek için önce nokta seçin.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
              <AramaKutusu etiket="Ürün ara" ipucu="Ürün adı veya barkod" deger={arama} degistir={setArama} />
              <p role="status" className="m-0 text-sm text-murekkep-2">
                {seciliKalemler.length} kalem seçili. Arama dışında kalan seçimler de tutanağa dahildir.
              </p>
              <SorguDurumu
                yukleniyor={Boolean(arama.trim()) && aramaSorgu.isPending}
                hata={arama.trim() ? aramaSorgu.error : null}
                bos={gorunenKalemler.length === 0}
                bosBaslik="Aramaya uygun mal yok"
                bosAciklama="Ürün adını veya tam barkodu kontrol edin."
                yenile={() => void aramaSorgu.refetch()}
              >
              <Tablo>
                <TabloBaslik>
                  <th scope="col">
                    <span className="gizli-erisilebilir">Seçim</span>
                  </th>
                  <th scope="col">Ürün</th>
                  <th scope="col">Bekleyen</th>
                  <th scope="col">Durum</th>
                  <th scope="col">İmha miktarı</th>
                </TabloBaslik>
                <TabloGovde>
                  {gorunenKalemler.map((k) => {
                    const secili = k.urunId in secimler;
                    const deger = secimler[k.urunId] ?? "";
                    const hata = secili
                      ? satirHatasi(deger, k.mevcut)
                      : undefined;
                    return (
                      <tr key={`${k.urunId}-${k.noktaId}`}>
                        <td>
                          <input
                            type="checkbox"
                            checked={secili}
                            onChange={(e) => secimDegistir(k, e.target.checked)}
                            aria-label={`${k.urunAd} kalemini tutanağa ekle`}
                            className="h-4 w-4 accent-simit"
                          />
                        </td>
                        <td className="font-semibold text-murekkep">
                          {k.urunAd}
                          {k.depoMu && (
                            <span className="ml-1 text-xs text-murekkep-3">
                              (depo)
                            </span>
                          )}
                        </td>
                        <td className="rakam text-right font-mono">
                          {miktarGoster(k.mevcut)}
                        </td>
                        <td>
                          <Rozet tur="hata">
                            {STOK_DURUM_ADI[StokDurumu.IMHA_BEKLIYOR]}
                          </Rozet>
                        </td>
                        <td>
                          {secili ? (
                            <Alan
                              etiket={`${k.urunAd} imha miktarı`}
                              value={deger}
                              onChange={(e) => {
                                const yeniDeger = e.target.value;
                                setOnayBekliyor(false);
                                setSecimler((onceki) => ({
                                  ...onceki,
                                  [k.urunId]: yeniDeger,
                                }));
                              }}
                              inputMode="decimal"
                              className="max-w-[8rem] text-right"
                              {...(gonderildi && hata ? { hata } : {})}
                            />
                          ) : (
                            <span className="text-sm text-murekkep-3">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </TabloGovde>
              </Tablo>
              </SorguDurumu>
              </div>
            )}
          </div>
        </SorguDurumu>
      </Kart>

      {noktaId !== "" && yazabilir && (
        <Kart baslik="Tutanak">
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              setGonderildi(true);
              if (seciliKalemler.length === 0) return;
              if (gecersizSatirVar) return;
              if (!aciklama.trim()) return;
              setOnayBekliyor(true);
            }}
          >
            <p className="m-0 text-sm text-murekkep-2">
              Seçili kalem:{" "}
              <strong className="rakam font-mono">
                {seciliKalemler.length}
              </strong>
              . Kısmi imha yapılabilir; kalan miktar imha bekliyor kondisyonunda
              kalır.
            </p>

            <MetinAlani
              etiket="Gerekçe (zorunlu)"
              value={aciklama}
              onChange={(e) => setAciklama(e.target.value)}
              rows={3}
              maxLength={500}
              ipucu="Tutanağın karşılığı budur: neden imha edildi, kim tespit etti, tarih/kondisyon durumu ne idi."
              {...(aciklamaHatasi ? { hata: aciklamaHatasi } : {})}
            />

            {gonderildi && seciliKalemler.length === 0 && (
              <p className="m-0 text-xs text-hata" role="alert">
                En az bir kalem seçilmelidir.
              </p>
            )}

            {gonder.error &&
            (yetkisizMi(gonder.error) || kapsamDisiMi(gonder.error)) ? (
              <Uyari tur="hata" baslik="Bu işlemi yapma yetkiniz yok">
                <p className="m-0">{YETKI_MESAJI}</p>
                <p className="m-0 mt-2">
                  Nokta sorumlusu ve kasiyer imha tutanağı açamaz — malı imhaya
                  ayıran ile imhayı onaylayanın aynı kişi olmaması esastır.
                </p>
              </Uyari>
            ) : (
              <IslemDurumu hata={gonder.error} />
            )}

            {!onayBekliyor && (
              <div>
                <Buton type="submit" tur="birincil">
                  Tutanağı hazırla
                </Buton>
              </div>
            )}
          </form>

          {onayBekliyor && (
            <div className="mt-4">
              <TehlikeliOnay
                soru="Seçili mallar imha edilsin mi?"
                aciklama={
                  <>
                    <p className="m-0">
                      Bu işlem <strong>geri alınamaz</strong>: stok düşer, imha
                      tutanağı yazılır ve maliyeti{" "}
                      <strong>fire/imha gideri</strong> olarak bu noktanın
                      kâr-zararına işlenir.
                    </p>
                    <p className="m-0 mt-1">
                      Yazılacak tutar sunucuda ortalama maliyetten hesaplanır ve
                      kaydın ardından burada gösterilir.
                    </p>
                  </>
                }
                onayMetni="Evet, imha et"
                onayla={() => gonder.mutate()}
                vazgec={() => setOnayBekliyor(false)}
                yukleniyor={gonder.isPending}
                cocuk={
                  <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
                    {seciliKalemler.map((k) => (
                      <li key={k.urunId}>
                        {k.urunAd} ·{" "}
                        <span className="rakam font-mono">
                          {miktarGoster(
                            miktarNormalize(secimler[k.urunId] ?? k.mevcut),
                          )}
                        </span>{" "}
                        <span className="text-murekkep-3">
                          (bekleyen: {miktarGoster(k.mevcut)})
                        </span>
                      </li>
                    ))}
                  </ul>
                }
              />
            </div>
          )}
        </Kart>
      )}
    </Sayfa>
  );
}
